import React, { useEffect, useRef, useState, useMemo } from "react";
import * as d3 from "d3";
import { Segmented, theme } from "antd";
import { GeneticWheelProps, WheelNode } from "../../../../types/geneCodes/geneticWheel";
import { processGeneticResults, filterSnpsWithGeneticData, getSnpRiskStatus, getSnpNumericRiskFactor, ProcessedGeneticResult } from "../../../../utils/geneCodes/geneticAnalysisUtils";
import { calculateNumericRiskScore, NumericRiskCounts } from "../../../../utils/geneCodes/riskScoringUtils";
import { GeneStage } from "../../../../types/geneCodes/GeneStageTypes";
import { WheelGeneRef } from "../../../../types/geneCodes/Panel";
import { getNodeColor, fitArcLabel, applyCentreTitle, CENTRE_TITLE_FIT, motionDuration, MOTION_MS } from "../../../../utils/geneCodes/wheelUtils";
import { geneRingForTrait } from "../../../../utils/geneCodes/wheelGeneRing";
import { WheelNodeKind, childKindOf, kindLabel } from "../../../../utils/geneCodes/wheelAuthoring";
import { isDarkTheme } from "../../../../utils/geneCodes/wheelColors";
import { matchedInterventions, applyModifier, rollupModifiedCount } from "../../../../utils/geneCodes/interventionOverlay";
import { useMeasuredBox } from "./useMeasuredBox";
import "./wheels.css";

// Module constant so the default does not change identity on every render and rebuild the wheel.
const NO_INTERVENTIONS: string[] = [];

// Keeps a label off the two ring edges.
const LABEL_RING_PAD = 8;

const RING_PADDING = 20;
const CENTRE_FONT_PX = 14;
const MIN_WHEEL_PX = 260;
const WHEEL_INSET = 24;

// Big enough to carry the centre label at every layer count.
const HOLE_RATIO = 0.28;

// The intervention ring sits this far OUTSIDE the rim — 0.25cm at 96dpi — and is drawn this thin.
// RING_PADDING (20) is what reserves room for it inside the viewBox.
const MODIFIER_RIM_GAP = 9;
const MODIFIER_RING_PX = 3;

// The dot marking a modified node, and how far inside that node's outer/trailing corner it sits.
const MODIFIER_DOT_PX = 3;
const MODIFIER_DOT_EDGE = 10;

// The inner ring has the least circumference AND the longest names, and a radial label's run IS the
// band thickness — so it needs markedly more than an even split.
const LAYER_WEIGHTS = [2, 1.15, 1];

// The slice the build-mode add wedge takes out of the ring, matching ZoomableSunburst.
const ADD_WEDGE_ANGLE = (18 * Math.PI) / 180;

const RING_DEPTH: Record<WheelNodeKind, number> = { panel: 0, trait: 1, gene: 2, snp: 3 };

const Sunburst: React.FC<GeneticWheelProps> = ({ selectedPanel, traits = [], snps = [], geneticResults = [], onTraitClick, onGeneClick, onSnpClick, width: widthProp = 750, height: heightProp = 750, ink = "dark", modifierMap, selectedInterventions = NO_INTERVENTIONS, onAddNode, hideUngenotyped = false }) => {
	const { ref: containerRef, box } = useMeasuredBox();
	// Held in a ref so a shell that re-creates the handler each render cannot re-run the whole repaint.
	const addNodeRef = useRef(onAddNode);
	addNodeRef.current = onAddNode;
	const addWedge = Boolean(onAddNode);
	// The props are the shell's reserve arithmetic; the measured stage is the box the wheel really has.
	const size = Math.max(MIN_WHEEL_PX, Math.min(box.width || widthProp, box.height || heightProp) - WHEEL_INSET);
	const width = size;
	const height = size;
	const svgRef = useRef<SVGSVGElement>(null);
	// Position of each node within its ring — only the parity matters, for the stagger.
	const ringIndexRef = useRef<Map<string, number>>(new Map());
	const wheelGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
	const centerGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
	const [wheelData, setWheelData] = useState<WheelNode | null>(null);
	const [activeNode, setActiveNode] = useState<WheelNode | null>(null);
	const [currentRotation, setCurrentRotation] = useState(0);

	// Only getNodeColor's no-risk fallback still needs this; every other colour comes from wheels.css.
	const { token } = theme.useToken();
	const isDarkMode = isDarkTheme(token.colorBgContainer);

	// State for visible layers
	const [visibleLayers, setVisibleLayers] = useState<{ [key: string]: boolean }>({
		trait: true,
		gene: false,
		snp: false,
	});

	// Multi-layer shows every ring at once, so the + follows the SELECTION: it adds a child of the
	// node last clicked, and of the wheel itself while nothing is selected.
	const wedgeParent = activeNode ?? wheelData;
	const wedgeKind = addWedge && wedgeParent ? childKindOf(wedgeParent.type as WheelNodeKind) : null;
	// A hidden layer gets no wedge — the + would otherwise point at a ring the author cannot see.
	const wedgeRing = wedgeKind && visibleLayers[wedgeKind] ? wedgeKind : null;

	const getEffectiveAngle = (originalAngle: number, currentRotation: number): number => {
		const rotationRadians: number = (currentRotation * Math.PI) / 180;
		let effectiveAngle: number = originalAngle + rotationRadians;
		while (effectiveAngle < 0) effectiveAngle += 2 * Math.PI;
		while (effectiveAngle >= 2 * Math.PI) effectiveAngle -= 2 * Math.PI;
		return effectiveAngle;
	};

	interface TextNode extends d3.HierarchyRectangularNode<WheelNode> {
		x0: number;
		x1: number;
	}

	interface TextSelection extends d3.Selection<SVGTextElement, TextNode, SVGGElement, unknown> {}

	const updateTextOrientation = (textSelection: TextSelection, currentRotation: number): void => {
		textSelection.attr("transform", (d: TextNode): string => {
			const angle: number = (d.x0 + d.x1) / 2;
			const radius: number = getCenteredTextRadius(d);
			const effectiveAngle: number = getEffectiveAngle(angle, currentRotation);
			const shouldFlip: boolean = effectiveAngle > Math.PI;
			const textAngle: number = (angle * 180) / Math.PI - 90;
			const flipRotation: number = shouldFlip ? 180 : 0;
			return `rotate(${textAngle}) translate(${radius},0) rotate(${flipRotation})`;
		});
	};

	// The visible layers share the whole ring between them, so the wheel fills its box at any layer count.
	const geometry = useMemo(() => {
		const weights = [visibleLayers.trait, visibleLayers.gene, visibleLayers.snp].map((visible, index) => (visible ? LAYER_WEIGHTS[index] : 0));
		const totalWeight = weights.reduce((sum, weight) => sum + weight, 0) || 1;
		const maxRadius = Math.max(RING_PADDING, Math.min(width, height) / 2 - RING_PADDING);
		const hole = maxRadius * HOLE_RATIO;
		const span = maxRadius - hole;

		// A hidden layer has zero weight, so the ones after it move inward and it occupies nothing.
		const edgeAfter = (layers: number) => hole + (span * weights.slice(0, layers).reduce((sum, weight) => sum + weight, 0)) / totalWeight;

		return {
			centerX: width / 2,
			centerY: height / 2,
			maxRadius,
			hole,
			ringOf: (depth: number) => ({ inner: edgeAfter(depth - 1), outer: edgeAfter(depth) }),
		};
	}, [width, height, visibleLayers]);

	// Text is centred across its ring, so the band is its budget; the tangential gap decides whether
	// it can be drawn at all.
	// Every named arc gets a label; a crowded ring staggers them rather than dropping any.
	const labelFitFor = (d: d3.HierarchyRectangularNode<WheelNode>) => {
		const ring = geometry.ringOf(d.depth);
		const band = ring.outer - ring.inner;
		return fitArcLabel(d.data.name, {
			run: band - LABEL_RING_PAD,
			band,
			arcWidth: d.x1 - d.x0,
			radius: (ring.inner + ring.outer) / 2,
			index: ringIndexRef.current.get(d.data.id) ?? 0,
		});
	};

	// ONE arc for the rings and for the add wedge — a second radius rule is how the two drift apart.
	const ringArc = () =>
		d3
			.arc<d3.HierarchyRectangularNode<WheelNode>>()
			.startAngle((d) => d.x0)
			.endAngle((d) => d.x1)
			.innerRadius((d) => (d.depth === 0 ? 0 : geometry.ringOf(d.depth).inner))
			.outerRadius((d) => (d.depth === 0 ? geometry.hole : geometry.ringOf(d.depth).outer));

	// The same contract ZoomableSunburst.addWedge implements: one dashed wedge on the ring being
	// authored, calling back with the PARENT node.
	const drawAddWedge = () => {
		const g = wheelGroupRef.current;
		if (!g) return;

		g.selectAll("path.add-wedge").remove();
		g.selectAll("text.add-wedge-label").remove();
		if (!wedgeRing || !wedgeParent) return;

		const depth = RING_DEPTH[wedgeRing];
		const ring = geometry.ringOf(depth);
		const span = { x0: -ADD_WEDGE_ANGLE / 2, x1: ADD_WEDGE_ANGLE / 2, depth } as d3.HierarchyRectangularNode<WheelNode>;
		const parent = wedgeParent;
		const label = `+ ${kindLabel(wedgeRing)}`;
		const band = ring.outer - ring.inner;
		const radius = (ring.inner + ring.outer) / 2;
		const fit = fitArcLabel(label, { run: band - LABEL_RING_PAD, band, arcWidth: ADD_WEDGE_ANGLE, radius, index: 0 });

		g.append("path")
			.attr("class", "add-wedge")
			.attr("d", ringArc()(span) ?? "")
			.on("click", (event: any) => {
				event.stopPropagation();
				addNodeRef.current?.(parent);
			})
			.append("title")
			.text(`Add a ${kindLabel(wedgeRing)} to ${parent.name}`);

		// The wedge is centred on the wheel's zero angle, so its label takes the same flip the ring's do.
		const flip = getEffectiveAngle(0, currentRotation) > Math.PI ? 180 : 0;

		g.append("text")
			.attr("class", "add-wedge-label")
			.attr("transform", `rotate(-90) translate(${radius},0) rotate(${flip})`)
			.attr("text-anchor", "middle")
			.attr("dominant-baseline", "central")
			.style("font-size", `${fit.fontSize}px`)
			.text(label);
	};

	const renderWheel = () => {
		if (!svgRef.current || !wheelData) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		svg.attr("viewBox", `0 0 ${width} ${height}`);

		const g = svg.append("g").attr("transform", `translate(${geometry.centerX},${geometry.centerY}) rotate(${currentRotation})`).attr("class", "wheel-rotation");

		const centerGroup = svg.append("g").attr("transform", `translate(${geometry.centerX},${geometry.centerY})`).attr("class", "center-group");

		wheelGroupRef.current = g;
		centerGroupRef.current = centerGroup;

		const partition = d3.partition<WheelNode>().size([2 * Math.PI, geometry.maxRadius]);
		const root = d3.hierarchy(wheelData);

		root.sum((d) => {
			if (!d.children || d.children.length === 0) {
				return d.value || 1;
			}
			return 0;
		});

		root.eachBefore((node) => {
			if (node.children && node.children.length > 0) {
				const childSum = node.children.reduce((sum, child) => sum + (child.value || 0), 0);
				if (childSum === 0) {
					node.data.value = 1;
					return;
				}
				if (node !== root) {
					const scaleFactor = node.value! / childSum;
					node.children.forEach((child) => {
						child.data.value = (child.value || 0) * scaleFactor;
					});
				}
			}
		});

		const partitionedRoot = partition(root);

		// The wedge takes its slice OUT of the circle rather than sitting on a segment. Reserved for the
		// whole of build mode: a slot that came and went with the selection would re-lay-out the wheel.
		if (addWedge) {
			const scale = (2 * Math.PI - ADD_WEDGE_ANGLE) / (2 * Math.PI);
			partitionedRoot.each((d) => {
				if (d.depth === 0) return;
				d.x0 = d.x0 * scale + ADD_WEDGE_ANGLE / 2;
				d.x1 = d.x1 * scale + ADD_WEDGE_ANGLE / 2;
			});
		}

		const visibleNodes = partitionedRoot.descendants().filter((d) => {
			if (d.depth === 0) return true;
			if (d.depth === 1) return visibleLayers.trait;
			if (d.depth === 2) return visibleLayers.gene;
			if (d.depth === 3) return visibleLayers.snp;
			return false;
		});

		const arc = ringArc();

		const slice = g
			.selectAll("path")
			.data(visibleNodes)
			.enter()
			.append("path")
			.attr("d", arc as any)
			.attr("fill", (d) => getNodeColor(d.data, isDarkMode))
			.attr("stroke-width", 1)
			.attr("class", (d) => `wheel-segment${d.data.id === activeNode?.id ? " active" : ""}${d.data.hiddenReason ? " is-hidden" : ""}`)
			.on("click", (event, d) => {
				event.stopPropagation();
				const angle = (d.x0 + d.x1) / 2;
				let targetRotation = -(((angle - Math.PI / 2) * 180) / Math.PI);

				const normalizedCurrent = ((currentRotation % 360) + 360) % 360;
				const normalizedTarget = ((targetRotation % 360) + 360) % 360;

				let clockwiseDiff = normalizedTarget - normalizedCurrent;
				if (clockwiseDiff < 0) clockwiseDiff += 360;

				let counterclockwiseDiff = normalizedCurrent - normalizedTarget;
				if (counterclockwiseDiff < 0) counterclockwiseDiff += 360;

				let rotationDiff: number;
				let newRotation: number;
				if (clockwiseDiff <= counterclockwiseDiff) {
					rotationDiff = clockwiseDiff;
					newRotation = currentRotation + rotationDiff;
				} else {
					rotationDiff = -counterclockwiseDiff;
					newRotation = currentRotation + rotationDiff;
				}

				if (wheelGroupRef.current) {
					wheelGroupRef.current
						.transition()
						.duration(motionDuration(MOTION_MS.slow))
						.ease(d3.easeCubicInOut)
						.attrTween("transform", () => {
							const interpolate = d3.interpolate(currentRotation, newRotation);
							return (t: number) => {
								const currentRotValue = interpolate(t);
								if (t === 1) {
									setCurrentRotation(newRotation);
									requestAnimationFrame(() => {
										updateTextOrientation(g.selectAll("text.wheel-label"), newRotation);
									});
								}
								return `translate(${geometry.centerX},${geometry.centerY}) rotate(${currentRotValue})`;
							};
						});
				}

				setActiveNode(d.data);

				if (d.data.type === "trait" && onTraitClick) {
					onTraitClick(d.data.data);
				} else if (d.data.type === "gene" && onGeneClick) {
					onGeneClick(d.data.name);
				} else if (d.data.type === "snp" && onSnpClick) {
					onSnpClick(d.data.data);
				}
			})
			.on("mouseover", function () {
				d3.select(this).transition().duration(motionDuration(MOTION_MS.fast)).attr("stroke-width", 2);
			})
			.on("mouseout", function () {
				d3.select(this).transition().duration(motionDuration(MOTION_MS.fast)).attr("stroke-width", 1);
			});

		// A ring too dense to label keeps its names reachable here.
		slice.append("title").text((d) => `${d.data.name}${d.data.hiddenReason ? " · hidden" : ""}`);

		// The marker tracks the DEEPEST visible layer: whole trait with traits alone, whole gene once
		// genes are shown, the SNP itself once SNPs are.
		const markerDepth = visibleLayers.snp ? 3 : visibleLayers.gene ? 2 : 1;
		const modified = visibleNodes.filter((d) => d.depth === markerDepth && (d.data.modifiedCount || 0) > 0);
		// One ring at the wheel's rim, whatever depth the modified node sits at, so the markers line up
		// instead of scattering across three ring edges.
		const bandArc = d3
			.arc<d3.HierarchyRectangularNode<WheelNode>>()
			.startAngle((d) => d.x0)
			.endAngle((d) => d.x1)
			.innerRadius(() => geometry.maxRadius + MODIFIER_RIM_GAP)
			.outerRadius(() => geometry.maxRadius + MODIFIER_RIM_GAP + MODIFIER_RING_PX);

		g.selectAll("path.wheel-modifier-band")
			.data(modified)
			.enter()
			.append("path")
			.attr("class", "wheel-modifier-band")
			.attr("d", bandArc as any)
			.attr("pointer-events", "none");

		const dotRadiusOf = (d: d3.HierarchyRectangularNode<WheelNode>) => {
			const ring = geometry.ringOf(d.depth);
			return ring.outer - Math.min(MODIFIER_DOT_EDGE, (ring.outer - ring.inner) / 2);
		};
		const dotAngleOf = (d: d3.HierarchyRectangularNode<WheelNode>, r: number) => d.x1 - Math.min((d.x1 - d.x0) / 2, MODIFIER_DOT_EDGE / Math.max(r, 1));

		// EVERY visible ring gets its dots, not just the deepest — the trait ring keeps them once genes
		// are shown. Only the SNP ring is excluded, where the dot lands on the rs label.
		const dotted = visibleNodes.filter((d) => d.depth >= 1 && d.depth <= 2 && (d.data.modifiedCount || 0) > 0);

		g.selectAll("circle.wheel-modifier-dot")
			.data(dotted)
			.enter()
			.append("circle")
			.attr("class", "wheel-modifier-dot")
			.attr("pointer-events", "none")
			.attr("r", MODIFIER_DOT_PX)
			// The node's outer/trailing corner — its "top right" once the wedge is read in its own rotation.
			.attr("cx", (d) => {
				const r = dotRadiusOf(d);
				return Math.sin(dotAngleOf(d, r)) * r;
			})
			.attr("cy", (d) => {
				const r = dotRadiusOf(d);
				return -Math.cos(dotAngleOf(d, r)) * r;
			});

		const byRing = new Map<number, d3.HierarchyRectangularNode<WheelNode>[]>();
		visibleNodes.filter((d) => d.depth > 0).forEach((d) => byRing.set(d.depth, [...(byRing.get(d.depth) ?? []), d]));
		ringIndexRef.current = new Map();
		byRing.forEach((ring) => [...ring].sort((a, b) => a.x0 - b.x0).forEach((d, i) => ringIndexRef.current.set(d.data.id, i)));

		// Only an arc with no name at all goes unlabelled.
		const text = g
			.selectAll("text")
			.data(visibleNodes.filter((d) => d.depth > 0 && labelFitFor(d).text !== ""))
			.enter()
			.append("text")
			.attr("class", "wheel-label")
			.attr("text-anchor", "middle")
			.attr("dominant-baseline", "central")
			.text((d) => labelFitFor(d).text)
			.style("font-size", (d) => `${labelFitFor(d).fontSize}px`)
			.style("font-weight", "normal")
			.on("mouseover", function () {
				d3.select(this).transition().duration(motionDuration(MOTION_MS.fast)).style("font-weight", "bold");
			})
			.on("mouseout", function (this: any, d: unknown) {
				const depth = (d as d3.HierarchyRectangularNode<WheelNode>).depth;
				d3.select(this)
					.transition()
					.duration(motionDuration(MOTION_MS.fast))
					.style("font-weight", depth <= 1 ? "bold" : "normal");
			});

		updateTextOrientation(text, currentRotation);

		if (selectedPanel) {
			centerGroup
				.append("text")
				.attr("text-anchor", "middle")
				.attr("dy", "0.35em")
				.attr("class", "center-label")
				.attr("font-size", `${CENTRE_FONT_PX}px`)
				.attr("pointer-events", "none")
				.text((selectedPanel.name_panel ?? "").toUpperCase())
				.each(function () {
					applyCentreTitle(this as SVGTextElement, selectedPanel.name_panel, geometry.hole * 2 * CENTRE_TITLE_FIT, CENTRE_FONT_PX);
				});
		}

		svg.on("click", () => {
			resetView();
		});

		drawAddWedge();
	};

	const animateRotation = (targetRotation: number) => {
		if (!wheelGroupRef.current || !centerGroupRef.current) return;

		const startRotation = currentRotation;
		const duration = motionDuration(MOTION_MS.slow);

		wheelGroupRef.current
			.transition()
			.duration(duration)
			.ease(d3.easeCubicInOut)
			.attrTween("transform", () => {
				const interpolate = d3.interpolate(startRotation, targetRotation);
				return (t: number) => {
					const currentRotValue = interpolate(t);
					if (t === 1) {
						setCurrentRotation(targetRotation);
						requestAnimationFrame(() => {
							if (wheelGroupRef.current) {
								updateTextOrientation(wheelGroupRef.current.selectAll("text.wheel-label"), targetRotation);
							}
						});
					}
					return `translate(${geometry.centerX},${geometry.centerY}) rotate(${currentRotValue})`;
				};
			});

		centerGroupRef.current.transition().duration(duration).ease(d3.easeCubicInOut).attr("transform", `translate(${geometry.centerX},${geometry.centerY})`);
	};

	const buildWheelData = () => {
		if (!selectedPanel) return null;

		const rootNode: WheelNode = {
			id: `panel-${selectedPanel.id_panels}`,
			name: selectedPanel.name_panel,
			description: selectedPanel.desc_short || undefined,
			type: "panel",
			value: 1,
			data: selectedPanel,
			children: [],
		};

		const hasGeneticData = geneticResults && geneticResults.length > 0;
		let processedGeneticResults: Map<string, ProcessedGeneticResult> = new Map();
		if (hasGeneticData) {
			processedGeneticResults = processGeneticResults(geneticResults as GeneStage[], snps);
		}

		const filteredSnps = filterSnpsWithGeneticData(snps, processedGeneticResults, hasGeneticData, hideUngenotyped);

		const snpsByTrait: Record<string, typeof filteredSnps> = {};

		traits.forEach((trait) => {
			snpsByTrait[trait.trait_name] = [];
		});

		filteredSnps.forEach((snp) => {
			if (snp.subcategory && snpsByTrait[snp.subcategory]) {
				snpsByTrait[snp.subcategory].push(snp);
			}
		});

		traits.forEach((trait) => {
			const traitSnps = snpsByTrait[trait.trait_name] || [];

			if (hasGeneticData && traitSnps.length === 0) {
				return;
			}

			const traitNode: WheelNode = {
				id: `trait-${trait.idtraits}`,
				name: trait.trait_name,
				description: trait.trait_desc || undefined,
				type: "trait",
				value: traitSnps.length || 1,
				data: trait,
				children: [],
				hiddenReason: trait.hidden_reason ?? null,
			};

			const traitNumericCounts: NumericRiskCounts = { total: 0, sum: 0, count: 0 };
			const traitHasOnlyOneSnp = traitSnps.length === 1;

			// The ONE ring derivation (wheelGeneRing.ts) — server ids key the nodes, names are the fallback.
			// R6's SNP-less nodes join the ring, but NOT once the SNPs are filtered to a patient's results:
			// there an unmatched gene row means no data for it, and an empty wedge would claim otherwise.
			geneRingForTrait(trait, traitSnps, !hasGeneticData).forEach((ring) => {
				const geneSnps = ring.snps;

				const geneNode: WheelNode = {
					id: ring.nodeId,
					name: ring.name,
					type: "gene",
					value: geneSnps.length || 1,
					data: { gene: ring.name, wheelGeneId: ring.wheelGeneId, registryRef: ring.registryRef },
					children: [],
					// A row-less gene is derived from visible SNPs only, so it has no reason of its own.
					hiddenReason: ring.wheelGeneId != null ? trait.genes?.find((row: WheelGeneRef) => row.id === ring.wheelGeneId)?.hidden_reason ?? null : null,
				};

				const geneNumericCounts: NumericRiskCounts = { total: 0, sum: 0, count: 0 };

				geneSnps.forEach((snp) => {
					const riskStatus = hasGeneticData ? getSnpRiskStatus(snp, processedGeneticResults) : null;
					const numericRiskFactor = hasGeneticData ? getSnpNumericRiskFactor(snp, processedGeneticResults) : 0;
					const snpValue = traitHasOnlyOneSnp ? 2 : 1;
					const matched = matchedInterventions(snp.rs, modifierMap as Map<string, string[]>, selectedInterventions);

					const snpNode: WheelNode = {
						id: `snp-${snp.rs}`,
						name: snp.rs || "",
						type: "snp",
						value: snpValue,
						data: snp,
						riskLevel: riskStatus,
						modifiedBy: matched,
						hiddenReason: snp.hidden_reason ?? null,
					};

					geneNode.children?.push(snpNode);

					if (hasGeneticData && numericRiskFactor > 0) {
						geneNumericCounts.sum += applyModifier(numericRiskFactor, matched.length > 0);
						geneNumericCounts.count += 1;
						geneNumericCounts.total += 1;
					}
				});

				if (traitHasOnlyOneSnp) {
					geneNode.value = geneSnps.length * 2;
				}

				if (hasGeneticData) {
					geneNode.riskLevel = calculateNumericRiskScore(geneNumericCounts, false);

					if (geneNumericCounts.count > 0) {
						const geneAverageRisk = geneNumericCounts.sum / geneNumericCounts.count;
						traitNumericCounts.sum += geneAverageRisk;
						traitNumericCounts.count += 1;
						traitNumericCounts.total += 1;
					}
				}

				traitNode.children?.push(geneNode);
			});

			if (traitHasOnlyOneSnp) {
				traitNode.value = traitSnps.length * 2;
			}

			if (hasGeneticData) {
				traitNode.riskLevel = calculateNumericRiskScore(traitNumericCounts, true);
			}

			if (traitNode.children && (traitNode.children.length > 0 || !hasGeneticData)) {
				rootNode.children?.push(traitNode);
			}
		});

		rollupModifiedCount(rootNode);

		return rootNode;
	};

	useEffect(() => {
		setWheelData(buildWheelData());
	}, [selectedPanel, traits, snps, geneticResults, modifierMap, selectedInterventions, hideUngenotyped]);

	// Split from the rebuild above: an intervention toggle must not throw the practitioner out of their drill-down.
	useEffect(() => {
		setActiveNode(null);
		if (currentRotation !== 0) {
			animateRotation(0);
		} else {
			setCurrentRotation(0);
		}
	}, [selectedPanel, traits, snps, geneticResults]);



	const resetView = () => {
		setActiveNode(null);
		animateRotation(0);
	};

	function getCenteredTextRadius(d: d3.HierarchyRectangularNode<WheelNode>): number {
		if (d.depth === 0) return 0;
		const ring = geometry.ringOf(d.depth);
		return (ring.inner + ring.outer) / 2 + labelFitFor(d).offset;
	}

	useEffect(() => {
		if (!wheelData) return;

		if (wheelGroupRef.current && centerGroupRef.current) {
			wheelGroupRef.current.transition().duration(motionDuration(MOTION_MS.slow)).ease(d3.easeCubicInOut).attr("transform", `translate(${geometry.centerX},${geometry.centerY}) rotate(${currentRotation})`);

			centerGroupRef.current.transition().duration(motionDuration(MOTION_MS.slow)).ease(d3.easeCubicInOut).attr("transform", `translate(${geometry.centerX},${geometry.centerY})`);
		}

		requestAnimationFrame(() => {
			renderWheel();
		});
	}, [wheelData, visibleLayers, geometry, currentRotation]);

	// The full repaint is deliberately NOT keyed on the selection — its transition would cancel the
	// click's own rotation tween — so the wedge alone follows it, on the g the repaint already built.
	useEffect(() => {
		if (!addWedge) return undefined;
		const frame = requestAnimationFrame(() => drawAddWedge());
		return () => cancelAnimationFrame(frame);
	}, [addWedge, wedgeParent, wedgeRing, geometry, currentRotation]);

	// Three-stage layer toggle component
	const getActiveLayerStage = () => {
		if (visibleLayers.trait && visibleLayers.gene && visibleLayers.snp) return 2; // All layers
		if (visibleLayers.trait && visibleLayers.gene) return 1; // Traits + Genes
		return 0; // Traits only
	};

	const setLayerStage = (stage: number) => {
		switch (stage) {
			case 0: // Traits only
				setVisibleLayers({ trait: true, gene: false, snp: false });
				break;
			case 1: // Traits + Genes
				setVisibleLayers({ trait: true, gene: true, snp: false });
				break;
			case 2: // All layers
				setVisibleLayers({ trait: true, gene: true, snp: true });
				break;
		}
	};

	const LayerToggle = () => {
		const activeStage = getActiveLayerStage();

		return (
			<div className="flex items-center gap-2">
				<Segmented
					size="small"
					value={activeStage}
					onChange={(value) => setLayerStage(value as number)}
					options={[
						{ label: "Traits", value: 0 },
						{ label: "Genes", value: 1 },
						{ label: "SNPs", value: 2 },
					]}
				/>
			</div>
		);
	};

	return (
		<div className={`flex flex-col h-full w-full overflow-hidden mg-wheel mg-wheel--ink-${ink}`} ref={containerRef}>
			<div className="flex justify-between items-center px-2 flex-shrink-0">
				<LayerToggle />
			</div>
			<div className="flex-1 flex items-center justify-center overflow-hidden">
				<svg ref={svgRef} className="mg-wheel__svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" />
			</div>
		</div>
	);
};

export default Sunburst;
